// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, Input, OnChanges} from '@angular/core';
import { ILicense, IVolume, VolumeStatus } from '../../../../types/subscription';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { replaceGuidsWithMountLetters } from '../../../../shared/guid-to-mount-letters';
import { MB, OPAL_STATE } from '../../../../types/drive';
import { ClipboardService } from '../../../../services/clipboard.service';

@Component({
    selector: 'app-data-protections',
    templateUrl: './data-protections.component.html',
    styleUrls: ['./data-protections.component.scss'],
    standalone: false
})
export class DataProtectionsComponent implements OnChanges {
    @Input()license: ILicense;

    disabledExtensions: string[] = [];
    protectedExtensions: string[] = [];
    lockedExtensions: string[] = [];
    disabledFolders: string[] = [];
    protectedFolders: string[] = [];
    encryptedProtectedFolders: string[] = [];
    encryptedProtectedDirs = [];
    lockedFolders: string[] = [];
    encryptedLockedFolders: string[] = [];
    deceptionFiles: string[] = [];
    hasPBA: boolean = false;
    encryptionMounts = {};
    hasEncryptionMounts: boolean = false;
    secureVaultVolumes = [];
    encryptedFolders = [];

    constructor(private snackBar: MatSnackBar,
                private readonly clipboard: ClipboardService,
                private translate: TranslateService) {}

    ngOnChanges(): void {
      this.setLocalDataProtectionsFromLicense(this.license);
    }

    getSortedStringArray(stringArray: string[]): string[] {
        return stringArray ? stringArray.sort((a, b) => a.localeCompare(b)) : [];
    }

    onCopyText(copyText: string) {
      this.clipboard.copyMessage(copyText, this.translate.instant('COPIED_TO_CLIPBOARD')).then(null);
    }

    setLocalDataProtectionsFromLicense(license: ILicense) {
      if (license && license.config) {
        const _license = replaceGuidsWithMountLetters(license);
        const volumes: IVolume[] = [].concat(
            ...(_license && _license.config && _license.config.disks
                ? _license.config?.disks?.map(d => d?.volumes)
                : [])
        );
        this.disabledExtensions = this.getSortedStringArray(_license.config.disabledExts);
        this.protectedExtensions = this.getSortedStringArray(_license.config.protectedExts);
        this.lockedExtensions = this.getSortedStringArray(_license.config.lockedExts);
        this.disabledFolders = this.getSortedStringArray(_license.config.disabledDirs);
        this.protectedFolders = this.getSortedStringArray(_license.config.protectedDirs);
        this.lockedFolders = this.getSortedStringArray(_license.config.lockedDirs);
        this.deceptionFiles = this.getSortedStringArray(_license.config.deceptionFiles);
        this.encryptedProtectedFolders = _license.config?.encryptedProtectedDirs ?
          this.getSortedStringArray(_license.config?.encryptedProtectedDirs.map(d => d?.dir)) : ['(None)'];
        this.encryptedLockedFolders = _license.config?.encryptedLockedDirs ?
          this.getSortedStringArray(_license.config?.encryptedLockedDirs.map(d => d?.dir)) : ['(None)'];
        this.hasPBA = _license.config?.disks?.some(d => d.pba_info && d.pba_info.installed === true);

        const fullyEncryptedVolumes = volumes.filter(v => v?.volume_encryption &&
          v?.volume_encryption.encrypt_status === VolumeStatus.FULLY_ENCRYPTED);

        this.encryptionMounts = fullyEncryptedVolumes.reduce((volumes, volume) => ({
          ...volumes,
          [volume.volume_encryption?.encryption ??
          this.translate.instant('unknown')]: [...(volumes[volume.mount] || []), `${volume.mount}:`]
        }), {});

        this.hasEncryptionMounts = Object.keys(this.encryptionMounts).length !== 0;

        const secureVaults = _license && _license.config && _license.config.disks ?
            _license.config?.disks?.filter(d => d.opal_state === OPAL_STATE.OPAL_NOT_PROVISIONED ||
                d.opal_state === OPAL_STATE.OPAL_CIGENT_PROVISIONED
          ).map(d => {
            d['volumes'] = d?.volumes?.filter(v => v?.mount && v?.mount !== '\u0000') ?? [];
            return d;
        }) : [];

        this.secureVaultVolumes = [].concat(...secureVaults.map(d => {
          return d.volumes.map(v => {
            const isLockedVolume = _license.config?.lockedVolumes?.some(lv => v.guid === lv) ?? false;
            const isProtectedVolume = _license.config?.protectedVolumes?.some(pv => pv === v.guid) ?? false;
            let protectionMode: string;
            if (isLockedVolume) {
              protectionMode = 'Always On';
            } else if (isProtectedVolume) {
              protectionMode = 'During Threats';
            } else {
              if (v.filesystem === 'FAT' && v.label === '' && v.size == 8 * MB) {
                protectionMode = 'Locked';
              } else {
                protectionMode = 'Unlocked';
              }
            }
            v['protectionMode'] = protectionMode;
            return v;
          });
        }));

        this.encryptedFolders = [];
        _license?.config?.encryptedLockedDirs?.forEach(d => {
          d['protectionMode'] = 'Always On';
          d['directoryWithKeys'] = `${d.dir} - ` + d?.key_data?.map(k => `${k.scope}(${k.name})`).join(', ');
          this.encryptedFolders.push(d);
        });

        _license?.config?.encryptedProtectedDirs?.forEach(d => {
          d['protectionMode'] = 'During Threats';
          d['directoryWithKeys'] = `${d.dir} - ` + d?.key_data?.map(k => `${k.scope}(${k.name})`).join(', ');
          this.encryptedFolders.push(d);
        });
      }
    }
}
